package com.example.monetix.utils

import androidx.compose.runtime.Composable

data class ReceiptData(val total: String?, val merchant: String?, val date: String?, val rawText: String? = null)

expect class ReceiptScanner {
    suspend fun scanImage(uriString: String): ReceiptData
}

@Composable
expect fun rememberReceiptScanner(): ReceiptScanner

// 🚀 IL VERO E ORIGINALE CERVELLONE DELLA TUA APP (POTENZIATO)
fun parseSmartReceiptText(fullText: String): ReceiptData {
    if (fullText.isBlank()) return ReceiptData(null, null, null, "NESSUN TESTO RILEVATO DALLA FOTOCAMERA")

    // 1. TROVA IL TOTALE
    val regexTotal = Regex("""\b\d+[.,]\d{2}\b""")
    val matches = regexTotal.findAll(fullText)
    var maxAmount = 0.0
    var bestMatch: String? = null

    for (match in matches) {
        val valueStr = match.value.replace(",", ".")
        val value = valueStr.toDoubleOrNull() ?: 0.0
        if (value > maxAmount) {
            maxAmount = value
            bestMatch = valueStr
        }
    }
    val total = bestMatch?.replace(".", ",")

    // 2. TROVA L'ESERCENTE (Logica Aggressiva per la Prima Riga)
    val lines = fullText.lines().map { it.trim() }.filter { it.isNotBlank() }
    val blacklist = listOf("p.iva", "piva", "via ", "viale ", "piazza ", "corso ", "tel", "www.", "documento", "scontrino", "fiscale", "ricevuta", "vendita", "totale", "resto", "contanti", "grazie", "arrivederci", "euro", "reparto", "cassa", "reparti")
    val companySuffixes = listOf("SRL", "S.R.L.", "SPA", "S.P.A.", "SNC", "SAS")
    var merchant: String? = null

    // TENTATIVO 1: Analizza le primissime 3 righe dando priorità assoluta alla riga 0
    for (i in 0 until minOf(lines.size, 3)) {
        val line = lines[i]
        val lower = line.lowercase()

        // Se non è un indirizzo o una P.IVA...
        if (blacklist.none { lower.contains(it) } && line.length > 2) {

            // Se ha una sigla societaria, è sicuramente lui
            if (companySuffixes.any { line.uppercase().contains(it) }) {
                merchant = cleanName(line)
                break
            }

            // Se è la primissima riga in assoluto e non ha troppi numeri (es. date o codici strani), prendila!
            if (i == 0 && line.count { it.isDigit() } < 3) {
                merchant = cleanName(line)
                break
            }

            // Se è la prima o seconda riga, ed è tutta lettere, prendila!
            if (i <= 1 && line.all { it.isLetter() || it.isWhitespace() }) {
                merchant = cleanName(line)
                break
            }
        }
    }

    // TENTATIVO 2: Fallback se la cima dello scontrino era illeggibile
    if (merchant == null) {
        for (i in 0 until minOf(lines.size, 5)) {
            val line = lines[i]
            val upper = line.uppercase()
            if (line == upper && !line.any { it.isDigit() } && line.length > 3) {
                if (blacklist.none { upper.contains(it) }) {
                    merchant = cleanName(line)
                    break
                }
            }
        }
    }

    // 3. TROVA LA DATA
    var date: String? = null
    val dateRegex = Regex("""\b(\d{2})[/-](\d{2})[/-](\d{2,4})\b""")
    val dateMatch = dateRegex.find(fullText)
    if (dateMatch != null) {
        val d = dateMatch.groupValues[1]
        val m = dateMatch.groupValues[2]
        var y = dateMatch.groupValues[3]
        if (y.length == 2) y = "20$y"
        date = "$d/$m/$y"
    }

    return ReceiptData(total, merchant, date, fullText)
}

private fun cleanName(raw: String): String {
    val companySuffixes = listOf("SRL", "S.R.L.", "SPA", "S.P.A.", "SNC", "SAS")
    var cleaned = raw.replace(Regex("""[^a-zA-Z0-9\s&'-]"""), "").trim()

    // Rimuove le sigle societarie per avere un nome più pulito (es. "Conad SRL" -> "Conad")
    companySuffixes.forEach { suffix ->
        cleaned = cleaned.replace(suffix, "", ignoreCase = true).trim()
    }

    return cleaned.split(" ").joinToString(" ") { it.lowercase().replaceFirstChar { char -> char.uppercase() } }
}